/**
 * channelVocabulary — the words and tokens that go with `ChannelState`.
 *
 * Everything that renders a channel status reads its label and its colour from
 * here, so "connected" means the same word and the same blue everywhere. Brand
 * tokens only (design.md §1): blue carries success, magenta carries warning,
 * grey carries "nothing yet".
 */
import type { ChannelState } from "@/types/channels/channel-status";

/** The customer-facing word for each state. Do not localise per surface. */
export const CHANNEL_STATE_LABEL: Record<ChannelState, string> = {
  connected: "Connected",
  attention: "Needs attention",
  not_connected: "Not set up",
  unreported: "Not reported yet",
};

/**
 * Brand tokens per state. `dot` is the strip pip, `pill` is the badge.
 * genie-primary is the brand's success colour (design.md: "brand stays even for
 * success — we use blue"), genie-secondary is the brand's warning colour.
 */
export const CHANNEL_STATE_TOKENS: Record<
  ChannelState,
  { dot: string; pill: string; text: string }
> = {
  connected: {
    dot: "bg-genie-primary",
    pill: "bg-genie-primary/10 text-genie-primary",
    text: "text-genie-primary",
  },
  attention: {
    dot: "bg-genie-secondary",
    pill: "bg-genie-secondary/10 text-genie-secondary",
    text: "text-genie-secondary",
  },
  not_connected: {
    dot: "bg-gray-300",
    pill: "bg-gray-100 text-gray-600",
    text: "text-gray-600",
  },
  unreported: {
    dot: "bg-gray-300",
    pill: "bg-gray-100 text-gray-500",
    text: "text-gray-500",
  },
};

/** Sort order when several channels are shown together. */
export const CHANNEL_ORDER = ["voice", "phone", "email", "chat", "sync"] as const;

/** "14 minutes ago" / "yesterday" / "on 3 Feb". Null-safe. */
export const relativeTime = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;

  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;

  return `on ${new Date(then).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
};
